import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';
import {
    Box,
    Button,
    Paper,
    Typography,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    Menu,
    ListItemIcon,
    ListItemText,
    Skeleton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../utils/permissions';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import FolderIcon from '@mui/icons-material/Folder';
// import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import MoreVertIcon from '@mui/icons-material/MoreVert';

// Import Breadcrumbs component
import Breadcrumbs from '../../global/Components/Breadcrumbs';

// Import services
import {
    getProjectFolders,
    getDocuments,
    getFolderPath,
    downloadDocument,
    deleteFolder,
    deleteDocument,
} from './services/closeoutDocsService';

// Import components
import UnifiedList from './components/UnifiedList';
import UnifiedGrid from './components/UnifiedGrid';
import CloseoutDocsSearch from './components/CloseoutDocsSearch';
import ItemNameDialog from './components/ItemNameDialog';
import UploadDialog from './components/UploadDialog';
import MoveDialog from './components/MoveDialog';
import OwnerAccessSettings from './components/OwnerAccessSettings';
import DeleteConfirmDialog from './components/DeleteConfirmDialog';
import CloseoutDocsHistory from './components/CloseoutDocsHistory';

/**
 * Main Closeout Docs page component
 * Provides UI for viewing and managing closeout documents and folders
 * Features hierarchical folder structure with breadcrumb navigation
 *
 * @returns {React.ReactElement} Closeout Docs page UI
 */
const CloseoutDocsPage = () => {
    const theme = useTheme();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { canProject, isLoading: permissionsLoading } = usePermissions();

    // Permission check for write actions (upload, new folder, delete, rename, move, settings)
    const canWrite = !permissionsLoading && canProject(projectId, PERMISSIONS.CLOSEOUT_DOCS_WRITE);

    // State for folders and documents
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [folderPath, setFolderPath] = useState([]);
    const [ownerAccessEnabled, setOwnerAccessEnabled] = useState(false);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Dialog states
    const [itemNameDialogOpen, setItemNameDialogOpen] = useState(false);
    const [itemNameDialogMode, setItemNameDialogMode] = useState('createFolder'); // 'createFolder', 'renameFolder', or 'renameDocument'
    const [itemToRename, setItemToRename] = useState(null);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveItem, setMoveItem] = useState(null);
    const [moveItemType, setMoveItemType] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteItemType, setDeleteItemType] = useState(null); // 'folder' or 'document'
    const [deleting, setDeleting] = useState(false);

    // Snackbar state for notifications
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // View mode state (grid or list) with localStorage persistence
    const [viewMode, setViewMode] = useState(() => {
        const savedView = localStorage.getItem('closeoutDocsViewMode');
        return savedView || 'list';
    });

    // Sort state with localStorage persistence
    const [sortBy, setSortBy] = useState(() => {
        const savedSortBy = localStorage.getItem('closeoutDocsSortBy');
        return savedSortBy || 'name';
    });

    const [sortOrder, setSortOrder] = useState(() => {
        const savedSortOrder = localStorage.getItem('closeoutDocsSortOrder');
        return savedSortOrder || 'asc';
    });

    // Refresh trigger to force data reload when needed
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Menu anchor for mobile more options menu
    const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
    const isMoreMenuOpen = Boolean(moreMenuAnchor);

    /**
     * Function to trigger a data refresh
     */
    const refreshData = useCallback(() => {
        setRefreshTrigger((prev) => prev + 1);
    }, []);

    /**
     * Sort folders and documents separately
     * Folders appear first (sorted), followed by documents (sorted)
     */
    const sortedFolders = React.useMemo(() => {
        const foldersCopy = [...folders];
        if (sortBy === 'name') {
            foldersCopy.sort((a, b) => {
                const nameA = a.folderName?.toLowerCase() || '';
                const nameB = b.folderName?.toLowerCase() || '';
                return sortOrder === 'asc'
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            });
        } else if (sortBy === 'date') {
            foldersCopy.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            });
        }
        return foldersCopy;
    }, [folders, sortBy, sortOrder]);

    const sortedDocuments = React.useMemo(() => {
        const documentsCopy = [...documents];
        if (sortBy === 'name') {
            documentsCopy.sort((a, b) => {
                const nameA = a.originalFilename?.toLowerCase() || '';
                const nameB = b.originalFilename?.toLowerCase() || '';
                return sortOrder === 'asc'
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            });
        } else if (sortBy === 'date') {
            documentsCopy.sort((a, b) => {
                const dateA = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
                const dateB = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            });
        }
        return documentsCopy;
    }, [documents, sortBy, sortOrder]);

    /**
     * Handle sort change
     */
    const handleSortChange = useCallback(
        (newSortBy) => {
            if (newSortBy === sortBy) {
                // Toggle sort order if clicking same column
                setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                // Set new sort column with appropriate default order
                setSortBy(newSortBy);
                if (newSortBy === 'name') {
                    setSortOrder('asc'); // A-Z for name
                } else if (newSortBy === 'date') {
                    setSortOrder('desc'); // Newest first for date
                }
            }
        },
        [sortBy]
    );

    /**
     * Persist view mode to localStorage
     */
    useEffect(() => {
        localStorage.setItem('closeoutDocsViewMode', viewMode);
    }, [viewMode]);

    /**
     * Persist sort preferences to localStorage
     */
    useEffect(() => {
        localStorage.setItem('closeoutDocsSortBy', sortBy);
        localStorage.setItem('closeoutDocsSortOrder', sortOrder);
    }, [sortBy, sortOrder]);

    /**
     * Sync currentFolderId state with URL query parameter
     * This is used for UI state (like showing back button)
     */
    useEffect(() => {
        const folderIdFromUrl = searchParams.get('folderId');
        setCurrentFolderId(folderIdFromUrl || null);
    }, [searchParams]);

    /**
     * Build breadcrumb links dynamically based on current folder path
     */
    const breadcrumbLinks = React.useMemo(() => {
        const links = [
            { path: '/projects', label: 'Projects' },
            { path: `/projects/${projectId}`, label: 'Project Dashboard' },
            { path: `/projects/${projectId}/closeout-docs`, label: 'Closeout Docs' },
        ];

        // Add folder path breadcrumbs
        folderPath.forEach((folder) => {
            // For intermediate folders, make them clickable
            // The last folder in the path is the current folder, so it's just displayed as text
            links.push({
                path: `/projects/${projectId}/closeout-docs?folderId=${folder.folderId}`,
                label: folder.folderName,
            });
        });

        return links;
    }, [projectId, folderPath]);

    /**
     * Load folders and documents for the current location
     * Reads folderId directly from URL to avoid race conditions
     */
    useEffect(() => {
        const loadData = async () => {
            if (!projectId) return;

            const folderIdFromUrl = searchParams.get('folderId');

            setLoading(true);
            setError(null);

            try {
                // Load folders and documents in parallel
                // TODO: Re-enable getOwnerAccessStatus when backend PermissionsService is implemented
                const [foldersData, documentsData] = await Promise.all([
                    getProjectFolders(projectId, folderIdFromUrl),
                    getDocuments(projectId, folderIdFromUrl),
                ]);

                setFolders(foldersData);
                setDocuments(documentsData);
                // setOwnerAccessEnabled - currently using default state value (false)

                // Load folder path for breadcrumbs if we're in a subfolder
                if (folderIdFromUrl) {
                    const pathData = await getFolderPath(folderIdFromUrl, projectId);
                    setFolderPath(pathData);
                } else {
                    setFolderPath([]);
                }
            } catch (err) {
                setError('Failed to load closeout documents');
                setSnackbar({
                    open: true,
                    message: 'Failed to load closeout documents',
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [projectId, searchParams, refreshTrigger]);

    /**
     * Navigate into a folder
     */
    const handleFolderClick = useCallback(
        (folderId) => {
            // Update URL query param - the useEffect will sync the state
            navigate(`/projects/${projectId}/closeout-docs?folderId=${folderId}`, {
                replace: true,
            });
        },
        [projectId, navigate]
    );

    /**
     * Handle item name dialog success (folder created/renamed or document renamed)
     */
    const handleItemNameSuccess = useCallback(() => {
        const messages = {
            createFolder: 'Folder created successfully',
            renameFolder: 'Folder renamed successfully',
            renameDocument: 'File renamed successfully',
        };

        setItemNameDialogOpen(false);
        setItemToRename(null);
        setItemNameDialogMode('createFolder');
        refreshData();
        setSnackbar({
            open: true,
            message: messages[itemNameDialogMode] || 'Operation completed successfully',
            severity: 'success',
        });
    }, [refreshData, itemNameDialogMode]);

    /**
     * Handle document uploaded
     */
    const handleDocumentUploaded = useCallback(() => {
        setUploadDialogOpen(false);
        refreshData(); // Refresh data
        setSnackbar({
            open: true,
            message: 'Document uploaded successfully',
            severity: 'success',
        });
    }, [refreshData]);

    /**
     * Handle settings updated
     * TODO: When backend is implemented, this should refresh from backend instead of just using local state
     */
    const handleSettingsUpdated = useCallback((newValue) => {
        setSettingsDialogOpen(false);
        setOwnerAccessEnabled(newValue); // Update local state only (not persisted)
        setSnackbar({
            open: true,
            message: 'Settings updated (preview mode - not saved)',
            severity: 'info',
        });
    }, []);

    /**
     * Handle create folder - opens create dialog
     */
    const handleCreateFolder = useCallback(() => {
        setItemNameDialogMode('createFolder');
        setItemToRename(null);
        setItemNameDialogOpen(true);
    }, []);

    /**
     * Handle folder rename - opens rename dialog
     */
    const handleFolderRename = useCallback((folder) => {
        setItemNameDialogMode('renameFolder');
        setItemToRename(folder);
        setItemNameDialogOpen(true);
    }, []);

    /**
     * Handle document rename - opens rename dialog
     */
    const handleDocumentRename = useCallback((document) => {
        setItemNameDialogMode('renameDocument');
        setItemToRename(document);
        setItemNameDialogOpen(true);
    }, []);

    /**
     * Handle folder delete - opens delete confirmation dialog
     */
    const handleFolderDelete = useCallback((folder) => {
        setItemToDelete(folder);
        setDeleteItemType('folder');
        setDeleteDialogOpen(true);
    }, []);

    /**
     * Handle document delete - opens delete confirmation dialog
     */
    const handleDocumentDelete = useCallback((document) => {
        setItemToDelete(document);
        setDeleteItemType('document');
        setDeleteDialogOpen(true);
    }, []);

    /**
     * Handle delete confirmation
     */
    const handleConfirmDelete = useCallback(async () => {
        if (!itemToDelete || !deleteItemType) return;

        setDeleting(true);
        try {
            if (deleteItemType === 'folder') {
                await deleteFolder(itemToDelete.folderId, projectId);
            } else {
                await deleteDocument(itemToDelete.documentId, projectId);
            }

            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setDeleteItemType(null);
            refreshData();
            setSnackbar({
                open: true,
                message:
                    deleteItemType === 'folder'
                        ? 'Folder deleted successfully'
                        : 'Document deleted successfully',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message:
                    deleteItemType === 'folder'
                        ? 'Failed to delete folder'
                        : 'Failed to delete document',
                severity: 'error',
            });
        } finally {
            setDeleting(false);
        }
    }, [itemToDelete, deleteItemType, projectId, refreshData]);

    /**
     * Handle folder move
     */
    const handleFolderMove = useCallback((folder) => {
        setMoveItem(folder);
        setMoveItemType('folder');
        setMoveDialogOpen(true);
    }, []);

    /**
     * Handle document click (open in viewer)
     */
    const handleDocumentClick = useCallback(
        (document) => {
            // Navigate to document viewer page
            navigate(`/projects/${projectId}/closeout-docs/view/${document.documentId}`, {
                state: {
                    document,
                    folderId: currentFolderId, // Preserve folder context for back navigation
                },
            });
        },
        [projectId, currentFolderId, navigate]
    );

    /**
     * Handle document download (from menu)
     */
    const handleDocumentDownload = useCallback(
        async (document) => {
            try {
                await downloadDocument(document.documentId, projectId, document.originalFilename);
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: 'Failed to download document',
                    severity: 'error',
                });
            }
        },
        [projectId]
    );

    /**
     * Handle document move
     */
    const handleDocumentMove = useCallback((document) => {
        setMoveItem(document);
        setMoveItemType('document');
        setMoveDialogOpen(true);
    }, []);

    /**
     * Handle move completed
     */
    const handleMoveCompleted = useCallback(() => {
        setMoveDialogOpen(false);
        setMoveItem(null);
        setMoveItemType(null);
        refreshData();
        setSnackbar({
            open: true,
            message: 'Item moved successfully',
            severity: 'success',
        });
    }, [refreshData]);

    /**
     * Handle opening project-level document history
     */
    const handleOpenProjectHistory = useCallback(() => {
        setHistoryDialogOpen(true);
    }, []);

    /**
     * Handle closing history dialog
     */
    const handleCloseHistory = useCallback(() => {
        setHistoryDialogOpen(false);
    }, []);

    /**
     * Close snackbar
     */
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    /**
     * Handle more menu open
     */
    const handleMoreMenuOpen = (event) => {
        setMoreMenuAnchor(event.currentTarget);
    };

    /**
     * Handle more menu close
     */
    const handleMoreMenuClose = () => {
        setMoreMenuAnchor(null);
    };

    /**
     * Handle more menu actions
     */
    const handleMoreMenuAction = (action) => {
        handleMoreMenuClose();
        switch (action) {
            case 'newFolder':
                handleCreateFolder();
                break;
            case 'history':
                handleOpenProjectHistory();
                break;
            case 'settings':
                setSettingsDialogOpen(true);
                break;
            default:
                break;
        }
    };

    /**
     * Handle document click from search results
     */
    const handleSearchDocumentClick = useCallback(
        (document) => {
            navigate(`/projects/${projectId}/closeout-docs/view/${document.documentId}`, {
                state: { document },
            });
        },
        [navigate, projectId]
    );

    /**
     * Handle folder click from search results
     */
    const handleSearchFolderClick = useCallback(
        (folder) => {
            navigate(`/projects/${projectId}/closeout-docs?folderId=${folder.folderId}`);
        },
        [navigate, projectId]
    );

    /**
     * Handle search errors
     */
    const handleSearchError = useCallback((message, severity = 'error') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    }, []);

    return (
        <Box
            sx={{
                width: '100%',
                overflowX: 'hidden',
                boxSizing: 'border-box',
            }}
        >
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2 }}>
                {/* Breadcrumbs */}
                <Grid size={{ xs: 12 }}>
                    <Breadcrumbs links={breadcrumbLinks} />
                </Grid>

                {/* Header Section */}
                <Grid size={{ xs: 12 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2,
                        }}
                    >
                        {/* Primary Action Buttons - Left */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                minWidth: 0,
                            }}
                        >
                            {/* View Toggle - Tablet/Desktop only */}
                            <Box
                                sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                    backgroundColor: theme.palette.background.paper,
                                }}
                            >
                                <Tooltip title="List View">
                                    <IconButton
                                        onClick={() => setViewMode('list')}
                                        sx={{
                                            borderRadius: 0,
                                            px: 1.5,
                                            py: 1,
                                            backgroundColor:
                                                viewMode === 'list'
                                                    ? theme.palette.primary.main
                                                    : 'transparent',
                                            color:
                                                viewMode === 'list'
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.secondary,
                                            '&:hover': {
                                                backgroundColor:
                                                    viewMode === 'list'
                                                        ? theme.palette.primary.dark
                                                        : theme.palette.action.hover,
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <ViewListIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Grid View">
                                    <IconButton
                                        onClick={() => setViewMode('grid')}
                                        sx={{
                                            borderRadius: 0,
                                            px: 1.5,
                                            py: 1,
                                            backgroundColor:
                                                viewMode === 'grid'
                                                    ? theme.palette.primary.main
                                                    : 'transparent',
                                            color:
                                                viewMode === 'grid'
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.secondary,
                                            '&:hover': {
                                                backgroundColor:
                                                    viewMode === 'grid'
                                                        ? theme.palette.primary.dark
                                                        : theme.palette.action.hover,
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <GridViewIcon fontSize="medium" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Sort Dropdown - Mobile/Tablet */}
                            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
                                <FormControl size="small">
                                    <Select
                                        value={`${sortBy}-${sortOrder}`}
                                        onChange={(e) => {
                                            const [newSortBy] = e.target.value.split('-');
                                            handleSortChange(newSortBy);
                                        }}
                                        sx={{
                                            backgroundColor: theme.palette.background.paper,
                                            borderRadius: { xs: 2.5, sm: 2 },
                                            border: `1px solid ${theme.palette.divider}`,
                                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                            minWidth: { xs: 140, sm: 180 },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                        }}
                                    >
                                        <MenuItem value="name-asc">Name (A-Z)</MenuItem>
                                        <MenuItem value="name-desc">Name (Z-A)</MenuItem>
                                        <MenuItem value="date-desc">Date (Newest)</MenuItem>
                                        <MenuItem value="date-asc">Date (Oldest)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* New Folder Button - Desktop only, requires CLOSEOUT_DOCS_WRITE */}
                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                {permissionsLoading ? (
                                    <Skeleton
                                        variant="rectangular"
                                        width={130}
                                        height={36}
                                        sx={{ borderRadius: 2 }}
                                    />
                                ) : (
                                    canWrite && (
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={handleCreateFolder}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: 2,
                                                flexShrink: 0,
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                px: 2.5,
                                                py: 0.75,
                                                borderColor: theme.palette.divider,
                                                color: theme.palette.text.primary,
                                                '&:hover': {
                                                    borderColor: theme.palette.primary.main,
                                                    backgroundColor: theme.palette.action.hover,
                                                },
                                            }}
                                        >
                                            New Folder
                                        </Button>
                                    )
                                )}
                            </Box>

                            {/* Upload Button - requires CLOSEOUT_DOCS_WRITE */}
                            {permissionsLoading ? (
                                <Skeleton
                                    variant="rectangular"
                                    width={100}
                                    height={36}
                                    sx={{ borderRadius: { xs: 3, sm: 2 } }}
                                />
                            ) : (
                                canWrite && (
                                    <Button
                                        variant="contained"
                                        startIcon={<UploadFileIcon />}
                                        onClick={() => setUploadDialogOpen(true)}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: { xs: 3, sm: 2 },
                                            flexShrink: 0,
                                            fontSize: { xs: '0.875rem', sm: '0.875rem' },
                                            fontWeight: 500,
                                            px: { xs: 2.5, sm: 3 },
                                            py: { xs: 1, sm: 0.75 },
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: theme.shadows[2],
                                            },
                                        }}
                                    >
                                        Upload
                                    </Button>
                                )
                            )}
                        </Box>

                        {/* Secondary/Utility Buttons - Right */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                minWidth: 0,
                            }}
                        >
                            {/* History Button - Desktop only, requires CLOSEOUT_DOCS_WRITE */}
                            {canWrite && (
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Tooltip title="View Document History">
                                        <IconButton
                                            onClick={handleOpenProjectHistory}
                                            sx={{
                                                color: theme.palette.grey[600],
                                                flexShrink: 0,
                                                fontSize: '1.25rem',
                                                p: 1,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 2,
                                                backgroundColor: theme.palette.background.paper,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                    borderColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            <HistoryIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}

                            {/* Settings Button - Desktop only, requires CLOSEOUT_DOCS_WRITE */}
                            {canWrite && (
                                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Tooltip title="Owner Access Settings">
                                        <IconButton
                                            onClick={() => setSettingsDialogOpen(true)}
                                            sx={{
                                                color: ownerAccessEnabled
                                                    ? theme.palette.success.main
                                                    : theme.palette.grey[500],
                                                flexShrink: 0,
                                                fontSize: '1.25rem',
                                                p: 1,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 2,
                                                backgroundColor: theme.palette.background.paper,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover,
                                                    borderColor: ownerAccessEnabled
                                                        ? theme.palette.success.main
                                                        : theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            <SettingsIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}

                            {/* More Menu Button - Mobile/Tablet only */}
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                <Tooltip title="More Options">
                                    <IconButton
                                        onClick={handleMoreMenuOpen}
                                        sx={{
                                            color: theme.palette.grey[600],
                                            flexShrink: 0,
                                            fontSize: { xs: '1.5rem', sm: '1.25rem' },
                                            p: { xs: 1.25, sm: 1 },
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: { xs: 2.5, sm: 2 },
                                            backgroundColor: isMoreMenuOpen
                                                ? theme.palette.action.selected
                                                : theme.palette.background.paper,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                                borderColor: theme.palette.primary.main,
                                                color: theme.palette.primary.main,
                                            },
                                        }}
                                    >
                                        <MoreVertIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                {/* Search Bar */}
                <Grid size={{ xs: 12 }}>
                    <CloseoutDocsSearch
                        projectId={projectId}
                        onDocumentClick={handleSearchDocumentClick}
                        onFolderClick={handleSearchFolderClick}
                        onError={handleSearchError}
                    />
                </Grid>

                {/* Content Section */}
                <Grid size={{ xs: 12 }}>
                    {loading ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                            }}
                        >
                            <Typography color="text.secondary">Loading...</Typography>
                        </Paper>
                    ) : error ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                            }}
                        >
                            <Typography color="error">{error}</Typography>
                        </Paper>
                    ) : folders.length === 0 && documents.length === 0 ? (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, sm: 4 },
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 300,
                                gap: 2,
                            }}
                        >
                            <FolderIcon sx={{ fontSize: 64, color: theme.palette.grey[300] }} />
                            <Typography color="text.secondary" variant="h6">
                                No folders or documents yet
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                                Create a folder or upload a document to get started
                            </Typography>
                        </Paper>
                    ) : (
                        <>
                            {/* Combined Folders and Documents List */}
                            {viewMode === 'list' ? (
                                <UnifiedList
                                    folders={sortedFolders}
                                    documents={sortedDocuments}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onSortChange={handleSortChange}
                                    onFolderClick={handleFolderClick}
                                    onDocumentClick={handleDocumentClick}
                                    onFolderRename={handleFolderRename}
                                    onFolderDelete={handleFolderDelete}
                                    onFolderMove={handleFolderMove}
                                    onDocumentRename={handleDocumentRename}
                                    onDocumentDownload={handleDocumentDownload}
                                    onDocumentDelete={handleDocumentDelete}
                                    onDocumentMove={handleDocumentMove}
                                    canWrite={canWrite}
                                />
                            ) : (
                                <UnifiedGrid
                                    folders={sortedFolders}
                                    documents={sortedDocuments}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onSortChange={handleSortChange}
                                    onFolderClick={handleFolderClick}
                                    onDocumentClick={handleDocumentClick}
                                    onFolderRename={handleFolderRename}
                                    onFolderDelete={handleFolderDelete}
                                    onFolderMove={handleFolderMove}
                                    onDocumentRename={handleDocumentRename}
                                    onDocumentDownload={handleDocumentDownload}
                                    onDocumentDelete={handleDocumentDelete}
                                    onDocumentMove={handleDocumentMove}
                                    canWrite={canWrite}
                                />
                            )}
                        </>
                    )}
                </Grid>
            </Grid>

            {/* Dialogs */}
            <ItemNameDialog
                open={itemNameDialogOpen}
                onClose={() => {
                    setItemNameDialogOpen(false);
                    setItemToRename(null);
                    setItemNameDialogMode('createFolder');
                }}
                onSuccess={handleItemNameSuccess}
                projectId={projectId}
                mode={itemNameDialogMode}
                parentFolderId={currentFolderId}
                item={itemToRename}
            />

            <UploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onSuccess={handleDocumentUploaded}
                projectId={projectId}
                folderId={currentFolderId}
            />

            <MoveDialog
                open={moveDialogOpen}
                onClose={() => setMoveDialogOpen(false)}
                onSuccess={handleMoveCompleted}
                projectId={projectId}
                item={moveItem}
                itemType={moveItemType}
            />

            <OwnerAccessSettings
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                onSuccess={handleSettingsUpdated}
                projectId={projectId}
                currentValue={ownerAccessEnabled}
            />

            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={() => {
                    if (!deleting) {
                        setDeleteDialogOpen(false);
                        setItemToDelete(null);
                        setDeleteItemType(null);
                    }
                }}
                onConfirm={handleConfirmDelete}
                itemName={
                    deleteItemType === 'folder'
                        ? itemToDelete?.folderName
                        : itemToDelete?.originalFilename
                }
                itemType={deleteItemType || 'item'}
                loading={deleting}
            />

            <CloseoutDocsHistory
                open={historyDialogOpen}
                onClose={handleCloseHistory}
                projectId={projectId}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* More Options Menu - Mobile/Tablet only */}
            <Menu
                anchorEl={moreMenuAnchor}
                open={isMoreMenuOpen}
                onClose={handleMoreMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        elevation: 3,
                        sx: {
                            minWidth: 200,
                            mt: 1,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                        },
                    },
                }}
            >
                {/* New Folder - requires CLOSEOUT_DOCS_WRITE */}
                {canWrite && (
                    <MenuItem
                        onClick={() => handleMoreMenuAction('newFolder')}
                        sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <ListItemIcon>
                            <AddIcon sx={{ color: theme.palette.text.primary }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="New Folder"
                            primaryTypographyProps={{
                                fontSize: '0.95rem',
                                fontWeight: 500,
                            }}
                        />
                    </MenuItem>
                )}
                {/* Document History - requires CLOSEOUT_DOCS_WRITE */}
                {canWrite && (
                    <MenuItem
                        onClick={() => handleMoreMenuAction('history')}
                        sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <ListItemIcon>
                            <HistoryIcon sx={{ color: theme.palette.grey[600] }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Document History"
                            primaryTypographyProps={{
                                fontSize: '0.95rem',
                                fontWeight: 500,
                            }}
                        />
                    </MenuItem>
                )}
                {/* Access Settings - requires CLOSEOUT_DOCS_WRITE */}
                {canWrite && (
                    <MenuItem
                        onClick={() => handleMoreMenuAction('settings')}
                        sx={{
                            py: 1.5,
                            px: 2,
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                    >
                        <ListItemIcon>
                            <SettingsIcon
                                sx={{
                                    color: ownerAccessEnabled
                                        ? theme.palette.success.main
                                        : theme.palette.grey[500],
                                }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primary="Access Settings"
                            primaryTypographyProps={{
                                fontSize: '0.95rem',
                                fontWeight: 500,
                            }}
                        />
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export default CloseoutDocsPage;
